import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../db';
import { addDoc, collection } from 'firebase/firestore';
import { db as firestore } from '../../firebase';

export const checkSurveyStatus = createAsyncThunk(
    'feedback/checkSurveyStatus',
    async (userId) => {
        if (!userId) return null;
        const surveyStatus = await db.surveyStatus.get(userId);
        return surveyStatus?.lastSurveyDate || null;
    }
);

export const submitFeedback = createAsyncThunk(
    'feedback/submitFeedback',
    async ({ rating, feedback, userId, requestFollowUp, userEmail, currentPage }) => {
        // Store feedback in Firestore
        await addDoc(collection(firestore, 'feedback'), {
            rating,
            feedback,
            userId,
            timestamp: new Date().toISOString(),
            requestFollowUp,
            userEmail: requestFollowUp ? userEmail : null,
            currentPage,
            buildSha: process.env.REACT_APP_BUILD_SHA || 'development'
        });

        // Store survey completion timestamp in IndexedDB
        const lastSurveyDate = new Date().toISOString();
        await db.surveyStatus.put({
            id: userId,
            lastSurveyDate
        });

        return { lastSurveyDate };
    }
);

export const feedbackSlice = createSlice({
    name: 'feedback',
    initialState: {
        showSurvey: false,
        shouldShowSurvey: false,
        rating: 0,
        feedback: '',
        submitting: false,
        submitted: false,
        lastSurveyDate: null,
        currentPage: null
    },
    reducers: {
        showSurvey: (state, action) => {
            state.showSurvey = true;
            state.shouldShowSurvey = true;
            if (action.payload?.overrideLastSurveyDate) {
                state.lastSurveyDate = null;
            }
        },
        hideSurvey: (state) => {
            state.showSurvey = false;
            state.rating = 0;
            state.feedback = '';
            state.submitted = false;
        },
        setRating: (state, action) => {
            state.rating = action.payload;
        },
        setFeedback: (state, action) => {
            state.feedback = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(checkSurveyStatus.fulfilled, (state, action) => {
                const lastSurveyDate = action.payload;
                state.lastSurveyDate = lastSurveyDate;

                if (!lastSurveyDate) {
                    state.shouldShowSurvey = true;
                    return;
                }

                const lastSurveyDateObj = new Date(lastSurveyDate);
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

                state.shouldShowSurvey = lastSurveyDateObj < oneMonthAgo;
            })
            .addCase(submitFeedback.pending, (state) => {
                state.submitting = true;
            })
            .addCase(submitFeedback.fulfilled, (state, action) => {
                state.submitting = false;
                state.submitted = true;
                state.lastSurveyDate = action.payload.lastSurveyDate;
            })
            .addCase(submitFeedback.rejected, (state) => {
                state.submitting = false;
                // Could add error handling here if needed
            });
    }
});

export const { showSurvey, hideSurvey, setRating, setFeedback, setCurrentPage } = feedbackSlice.actions;

export default feedbackSlice.reducer; 